<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Mres3API from 'dashboard/api/mres3';
import RuntimeAPI from 'dashboard/api/mres3Runtime';
import en from './en.json';
import ru from './ru.json';
import { eventLabel } from './eventLabel';
import { startMessages } from './startSchedule';

const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...startMessages.en },
    ru: { ...ru, ...startMessages.ru },
  },
});
const labels = [
  'active',
  'waiting',
  'blocked',
  'failed',
  'completed',
  'exited',
  'dispatched',
  'outside_test_allowlist',
  'appointment_patient_changed',
  'recipient_identity_unresolved',
];
const statusLabel = value =>
  labels.includes(value)
    ? t(`runtime.statuses.${value}`)
    : t('runtime.code', { code: value });
const connections = ref([]);
const connectionId = ref(null);
const state = ref(null);
const busy = ref(false);
const notice = ref('');
const error = ref('');
const routeJson = ref('[]');
const patientId = ref('');
const contactInboxId = ref(null);
const confirmIdentity = ref(false);

const apply = data => {
  state.value = data;
  state.value.settings = {
    enabled: false,
    mode: 'test',
    timezone: 'Europe/Moscow',
    test_patient_ids: [],
    routes: [],
    ...data.settings,
  };
  routeJson.value = JSON.stringify(state.value.settings.routes, null, 2);
};
const act = async callback => {
  busy.value = true;
  error.value = '';
  notice.value = '';
  try {
    apply((await callback()).data);
  } catch (failure) {
    error.value = failure.response?.data?.error
      ? t('runtime.requestFailed', { code: failure.response.data.error })
      : t('error');
  } finally {
    busy.value = false;
  }
};
const load = () => act(() => RuntimeAPI.show(connectionId.value));
onMounted(async () => {
  try {
    connections.value = (await Mres3API.get()).data;
    connectionId.value = connections.value[0]?.id || null;
    if (connectionId.value) await load();
  } catch (failure) {
    error.value = t('error');
  }
});
const save = async () => {
  let routes;
  try {
    routes = JSON.parse(routeJson.value);
  } catch (failure) {
    error.value = t('runtime.invalidJson');
    return;
  }
  if (
    state.value.settings.enabled &&
    !window.confirm(t('runtime.enableConfirm'))
  )
    return;
  await act(() =>
    RuntimeAPI.save(connectionId.value, {
      ...state.value.settings,
      routes,
      mode: 'test',
    })
  );
  if (!error.value) notice.value = t('runtime.saved');
};
const bind = () =>
  act(() =>
    RuntimeAPI.bind(connectionId.value, {
      patient_mres_id: Number(patientId.value),
      contact_inbox_id: contactInboxId.value,
      confirm_identity: confirmIdentity.value,
    })
  );
const addRoute = () => {
  try {
    const routes = JSON.parse(routeJson.value);
    routes.push({
      mis_number: null,
      branch_mis_id: '',
      department_mis_id: '',
      workplace_mis_id: '',
      inbox_id: null,
      variables: {
        department_address: '',
        department_phone: '',
        department_hours: '',
        department_notice: '',
        feedback_url: '',
        review_url: '',
      },
    });
    routeJson.value = JSON.stringify(routes, null, 2);
  } catch (failure) {
    error.value = t('runtime.invalidJson');
  }
};
</script>

<template>
  <section
    class="p-5 mt-8 space-y-5 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div>
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('runtime.title') }}
      </h2>
      <p class="mt-2 text-sm text-n-slate-11">{{ t('runtime.hint') }}</p>
    </div>
    <p v-if="error" role="alert" class="text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="notice" role="status" class="text-sm text-n-teal-11">
      {{ notice }}
    </p>
    <select
      v-model="connectionId"
      :disabled="busy"
      class="w-full text-sm"
      :aria-label="t('source')"
      @change="load"
    >
      <option
        v-for="connection in connections"
        :key="connection.id"
        :value="connection.id"
      >
        {{ connection.companies_uuid }}
      </option>
    </select>
    <template v-if="state">
      <label class="flex items-center gap-2 text-sm"
        ><input v-model="state.settings.enabled" type="checkbox" />{{
          t('runtime.enable')
        }}</label
      >
      <label class="block text-sm">
        <span>{{ t('timezone') }}</span>
        <input v-model="state.settings.timezone" class="w-full mt-1 text-sm" />
      </label>
      <div class="space-y-2">
        <h3 class="font-medium text-n-slate-12">{{ t('runtime.bindings') }}</h3>
        <label class="block text-sm"
          >{{ t('runtime.patientId')
          }}<input
            v-model="patientId"
            type="number"
            min="1"
            class="w-full mt-1 text-sm"
        /></label>
        <label class="block text-sm"
          >{{ t('runtime.dialog') }}
          <select v-model="contactInboxId" class="w-full mt-1 text-sm">
            <option
              v-for="contact in state.contacts"
              :key="contact.id"
              :value="contact.id"
            >
              {{ contact.name }} ·
              {{ state.sources.find(s => s.id === contact.inbox_id)?.name }}
            </option>
          </select>
        </label>
        <label class="flex gap-2 text-xs leading-5"
          ><input v-model="confirmIdentity" type="checkbox" />{{
            t('runtime.identityConfirm')
          }}</label
        >
        <button
          type="button"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak"
          :disabled="busy || !confirmIdentity || !patientId || !contactInboxId"
          @click="bind"
        >
          {{ t('runtime.bind') }}
        </button>
        <div
          v-for="binding in state.bindings"
          :key="binding.id"
          class="flex items-center justify-between gap-3 p-2 text-sm border rounded-lg border-n-weak"
        >
          <label class="flex items-center gap-2"
            ><input
              v-model="state.settings.test_patient_ids"
              type="checkbox"
              :value="binding.patient_id"
              :disabled="!binding.active"
            />{{ binding.patient_name }} ·
            {{ t('runtime.sourceRecord', { id: binding.patient_mres_id }) }}
            ·
            {{
              binding.active ? t('runtime.allowed') : t('runtime.revoked')
            }}</label
          >
          <button
            v-if="binding.active"
            type="button"
            :disabled="busy"
            @click="act(() => RuntimeAPI.revoke(connectionId, binding.id))"
          >
            {{ t('runtime.revoke') }}
          </button>
        </div>
      </div>
      <div>
        <h3 class="font-medium text-n-slate-12">{{ t('runtime.routes') }}</h3>
        <p class="my-2 text-xs leading-5 text-n-slate-11">
          {{ t('runtime.routesHint') }}
        </p>
        <p class="mb-2 text-xs text-n-slate-11">
          {{
            state.sources
              .map(source => `${source.id}: ${source.name}`)
              .join(' · ')
          }}
        </p>
        <textarea
          v-model="routeJson"
          rows="15"
          class="w-full font-mono text-xs"
          :aria-label="t('runtime.routes')"
        />
        <button
          type="button"
          class="px-3 py-2 text-sm border rounded-lg border-n-weak"
          @click="addRoute"
        >
          {{ t('runtime.addRoute') }}
        </button>
      </div>
      <div class="flex gap-3">
        <button
          type="button"
          class="px-4 py-2 text-sm text-white rounded-lg bg-n-brand"
          :disabled="busy"
          @click="save"
        >
          {{ t('runtime.save') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm border rounded-lg border-n-weak"
          :disabled="busy"
          @click="load"
        >
          {{ t('runtime.refresh') }}
        </button>
      </div>
      <div>
        <h3 class="mb-2 font-medium text-n-slate-12">
          {{ t('runtime.journal') }}
        </h3>
        <p v-if="!state.events.length" class="text-sm text-n-slate-11">
          {{ t('runtime.noEvents') }}
        </p>
        <div
          v-for="event in state.events"
          :key="event.id"
          class="py-2 text-xs border-b border-n-weak"
        >
          #{{ event.id }} ·
          {{
            t(
              event.patient_mres_id
                ? 'startSchedule.patientRecord'
                : 'runtime.sourceRecord',
              { id: event.patient_mres_id || event.appointment_mres_id }
            )
          }}
          · {{ eventLabel(event.event_type, t) }} ·
          {{
            event.outcome ? statusLabel(event.outcome) : t('runtime.pending')
          }}
        </div>
        <details
          v-for="run in state.runs"
          :key="run.id"
          class="py-2 text-xs border-b border-n-weak"
        >
          <summary>
            #{{ run.id }} · {{ t('runtime.scenario') }} {{ run.scenario_id }} ·
            {{ statusLabel(run.status) }}
            <span v-if="run.execution_error"
              >· {{ t('runtime.code', { code: run.execution_error }) }}</span
            >
          </summary>
          <p v-if="run.wait_until">
            {{ t('runtime.waitUntil') }} {{ run.wait_until }}
          </p>
          <div
            v-for="(step, index) in run.execution_trace"
            :key="index"
            class="mt-1"
          >
            {{
              t('runtime.traceStep', {
                node: step.node,
                result: step.result,
                at: step.at,
              })
            }}
          </div>
        </details>
      </div>
    </template>
  </section>
</template>
